/**
 * @fileoverview User directory filters.
 * @description The filter bar above the User Management table: the search box (handed in by the
 * page, which owns the Sales table kit this feature may not import), the Role, Reporting Manager,
 * State and Status selects, a Clear button that stays mounted so toggling filters never flips the
 * row between one and two lines.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 * @author Anshul Rokde
 */

import { useTranslation } from 'react-i18next'

import { ALL_FILTER } from '@/features/users/hooks/useUserDirectoryFilters'
import { DIRECTORY_STATUS_LABEL_KEY } from '@/features/users/utils/user-display.utils'
import Select from '@/shared/components/atoms/Select'

import type { useUserDirectoryFilters } from '@/features/users/hooks/useUserDirectoryFilters'
import type {
  DirectoryStatus,
  ReportingManagerOption,
  RoleOption,
  StateFilterOption,
} from '@/features/users/types/users.types'
import type { ReactNode } from 'react'

const STATUS_FILTERS: DirectoryStatus[] = ['ACTIVE', 'INACTIVE', 'PENDING']

interface UserDirectoryFiltersProps {
  filters: ReturnType<typeof useUserDirectoryFilters>
  choices: {
    roles: RoleOption[]
    managers: ReportingManagerOption[]
    states: StateFilterOption[]
  }
  /** The search box. */
  search: ReactNode
}

/**
 * @component
 * @param {UserDirectoryFiltersProps} props - The filter state, the choices, and the page's slots.
 * @param {ReturnType<typeof useUserDirectoryFilters>} props.filters - The filter controls.
 * @param {object} props.choices - The Role, Reporting Manager and State choices.
 * @param {ReactNode} props.search - The search box.
 */
function UserDirectoryFilters({ filters, choices, search }: UserDirectoryFiltersProps) {
  const { t } = useTranslation()
  const { values, anyFilter } = filters

  return (
    <div className="flex flex-wrap items-center gap-2.5 p-3.5 sm:p-4">
      {search}
      <Select
        aria-label={t('sales.userManagement.filters.allRoles')}
        data-testid="user-management-role-select"
        value={values.roleId}
        onChange={filters.changeRole}
        options={[
          { value: ALL_FILTER, label: t('sales.userManagement.filters.allRoles') },
          ...choices.roles.map((role) => ({ value: role.roleId, label: role.name })),
        ]}
      />
      <Select
        aria-label={t('sales.userManagement.filters.allReportingManagers')}
        data-testid="user-management-reporting-manager-select"
        value={values.managerId}
        onChange={filters.changeManager}
        options={[
          { value: ALL_FILTER, label: t('sales.userManagement.filters.allReportingManagers') },
          ...choices.managers.map((manager) => ({
            value: manager.userId,
            label: manager.displayName,
          })),
        ]}
      />
      <Select
        aria-label={t('sales.userManagement.filters.allStates')}
        data-testid="user-management-state-select"
        value={values.stateId}
        onChange={filters.changeState}
        options={[
          { value: ALL_FILTER, label: t('sales.userManagement.filters.allStates') },
          ...choices.states.map((state) => ({ value: state.value, label: state.label })),
        ]}
      />
      <Select
        aria-label={t('sales.userManagement.filters.allStatus')}
        data-testid="user-management-status-select"
        value={values.status}
        onChange={filters.changeStatus}
        options={[
          { value: ALL_FILTER, label: t('sales.userManagement.filters.allStatus') },
          ...STATUS_FILTERS.map((code) => ({
            value: code,
            label: t(DIRECTORY_STATUS_LABEL_KEY[code]),
          })),
        ]}
      />
      {/* Always mounted so toggling filters never flips this row between one/two lines. */}
      <button
        type="button"
        onClick={filters.clear}
        data-testid="user-management-clear-filters-button"
        disabled={!anyFilter}
        aria-hidden={!anyFilter}
        className={`rounded-lg border border-input-border bg-white px-3.5 py-2 text-[13px] font-semibold text-slate ${
          anyFilter ? 'cursor-pointer hover:border-border-hover hover:text-ink' : 'invisible'
        }`}
      >
        {t('sales.userManagement.filters.clear')}
      </button>
    </div>
  )
}

export default UserDirectoryFilters
